import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Anchor,
  Button,
  Checkbox,
  Container,
  Divider,
  Grid,
  Group,
  MultiSelect,
  PasswordInput,
  SegmentedControl,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { handleSubmitError } from '@utils/forms';
import { getMarketCategoryColor } from '@utils/mappers';

import { CreateCustomerRequestBody } from '@interfaces/customer.interface';

import { useCreateCustomerMutation } from '@api/customer.api';
import { useGetMarketCategoriesQuery } from '@api/marketCategory.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useFormEnhanced from '@hooks/useFormEnhanced';
import useGRecaptcha from '@hooks/useGRecaptcha';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from './common/locations';
import CompanyRoleSelector from './CompanyRoleSelector';
import DiseaseSelector from './DiseaseSelector';
import GoodfellowSelector from './GoodfellowSelector';
import InterestSectorSelector from './InterestSectorSelector';
import OrganizationTypeSelector from './OrganizationTypeSelector';
import StrongPasswordInput from './StrongPasswordInput';
import TechniqueSelector from './TechniqueSelector';

export default function RegisterForm() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { t } = useTranslation();
  const { getRecaptchaToken } = useGRecaptcha();

  const [params, setParams] = useQueryParams();
  const market = useMemo(() => params.get('market'), [params]);

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    email: '',
    emailConfirmation: '',
    password: '',
    passwordConfirmation: '',
    firstName: '',
    lastName: '',
    phone: '',
    company: '',
    companyRoleId: undefined,
    department: '',
    vatNumber: '',
    laboratory: '',
    address: '',
    postalCode: '',
    city: '',
    province: '',
    country: '',
    website: '',
    interestSector: [],
    techniquesUsed: [],
    goodfellow: [],
    disease: [],
    organizationType: [],
    additionalNotes: '',
    allowedCategories: market ? [market] : ([] as string[]),
  };

  const form = useFormEnhanced({
    initialValues,
    validate: {
      email: (val) =>
        /^\S+@\S+$/.test(val) ? null : t('register.validation.invalidEmail'),
      emailConfirmation: (val, values) =>
        val === values.email ? null : t('register.validation.emailsDoNotMatch'),
      passwordConfirmation: (val, values) =>
        val === values.password
          ? null
          : t('register.validation.passwordsDoNotMatch'),
      allowedCategories: (val) =>
        val.length > 0 ? null : t('register.validation.marketRequired'),
    },
    normalizeFields: {
      capitalizeFields: [
        'firstName',
        'lastName',
        'company',
        'department',
        'laboratory',
      ],
      uppercaseFields: ['vatNumber'],
      lowercaseFields: ['email'],
    },
  });

  // ==========================================================================
  // Api
  // ==========================================================================
  const [createCustomer, { isLoading }] = useCreateCustomerMutation();
  const { data: marketCategories = [], isLoading: isMarketCategoriesLoading } =
    useGetMarketCategoriesQuery();

  // Default market: prima categoria (Alimentare) se non c'è il parametro ?market=
  useEffect(() => {
    if (!market && marketCategories.length > 0) {
      form.setFieldValue('allowedCategories', ['Ricerca']);
    }
  }, [marketCategories]);

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const data: CreateCustomerRequestBody = {
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone.trim() === '' ? undefined : values.phone,
        company: values.company,
        companyRoleId: values.companyRoleId ? +values.companyRoleId : undefined,
        department:
          values.department.trim() === '' ? undefined : values.department,
        vatNumber: values.vatNumber.trim(),
        laboratory:
          values.laboratory.trim() === '' ? undefined : values.laboratory,
        address: values.address,
        postalCode: values.postalCode,
        city: values.city,
        province: values.province,
        country: values.country,
        website: values.website.trim() === '' ? undefined : values.website,
        interestSector:
          form.values.allowedCategories[0] !== 'Ricerca'
            ? values.interestSector.map((id) => +id)
            : [],
        techniquesUsed:
          form.values.allowedCategories[0] === 'Ricerca'
            ? values.techniquesUsed
            : [],
        goodfellow:
          form.values.allowedCategories[0] === 'Ricerca'
            ? values.goodfellow
            : [],
        organizationType:
          form.values.allowedCategories[0] === 'Ricerca'
            ? values.organizationType
            : [],
        disease:
          form.values.allowedCategories[0] === 'Ricerca' ? values.disease : [],
        allowedCategories: values.allowedCategories,
        additionalNotes:
          values.additionalNotes.trim() === ''
            ? undefined
            : values.additionalNotes,
      };

      const recaptchaToken = await getRecaptchaToken('SignUp');

      await createCustomer({ body: data, recaptchaToken }).unwrap();

      params.set('p', 'login');
      params.set('alert', t('register.successMessage'));
      setParams(params);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container w="100%" size="70rem">
      <Text ta="center" fw="bold" size="1.5rem">
        {t('register.title')}
      </Text>

      <Text ta="center" mt="md">
        {t('register.alreadyHaveAccount')}{' '}
        <Text
          span
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            params.set('p', 'login');
            setParams(params);
          }}
        >
          {t('register.signIn')}
        </Text>
      </Text>

      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <Grid gutter="xl" mt="md">
          <Grid.Col
            span={12}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Text c="brand" fw={500} ta="center">
              {t('register.benefitMessage')}
            </Text>

            <Text size="lg" fw="bold" mt="md">
              {t('register.selectMarketTitle')}{' '}
              <Text span c="red">
                *
              </Text>
            </Text>
            <SegmentedControl
              mt="sm"
              data={marketCategories.map((category) => ({
                label: category.name,
                value: category.name,
              }))}
              color={getMarketCategoryColor(form.values.allowedCategories[0])}
              value={form.values.allowedCategories[0]}
              onChange={(value) => {
                form.setFieldValue('allowedCategories', [value]);
              }}
              disabled={isMarketCategoriesLoading}
            />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <Grid>
              <Grid.Col span={12}>
                <Divider
                  label={t('register.accessInfoLabel')}
                  labelPosition="left"
                />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  type="email"
                  label={t('login.email')}
                  placeholder={t('login.emailPlaceholder')}
                  required
                  {...form.getInputProps('email')}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  type="email"
                  label={t('register.repeatEmail')}
                  placeholder={t('login.emailPlaceholder')}
                  required
                  {...form.getInputProps('emailConfirmation')}
                />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                <StrongPasswordInput
                  label={t('login.password')}
                  placeholder={t('login.passwordPlaceholder')}
                  required
                  {...form.getInputProps('password')}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                <PasswordInput
                  label={t('register.repeatPassword')}
                  placeholder={t('login.passwordPlaceholder')}
                  required
                  {...form.getInputProps('passwordConfirmation')}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <Grid>
              <Grid.Col span={12}>
                <Divider
                  label={t('register.basicInfoLabel')}
                  labelPosition="left"
                />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('register.firstName')}
                  placeholder={t('register.firstNamePlaceholder')}
                  required
                  {...form.getInputProps('firstName')}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('register.lastName')}
                  placeholder={t('register.lastNamePlaceholder')}
                  required
                  {...form.getInputProps('lastName')}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('register.phone')}
                  placeholder={t('register.phonePlaceholder')}
                  type="tel"
                  {...form.getInputProps('phone')}
                />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('register.companyName')}
                  placeholder={t('register.companyNamePlaceholder')}
                  required
                  {...form.getInputProps('company')}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('register.vatNumber')}
                  placeholder={t('register.vatNumberPlaceholder')}
                  required
                  {...form.getInputProps('vatNumber')}
                />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                <CompanyRoleSelector
                  {...form.getInputProps('companyRoleId')}
                  marketCategoryName={form.values.allowedCategories[0]}
                />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('register.department')}
                  placeholder={t('register.departmentPlaceholder')}
                  {...form.getInputProps('department')}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('register.laboratory')}
                  placeholder={t('register.laboratoryPlaceholder')}
                  {...form.getInputProps('laboratory')}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <Grid>
              <Grid.Col span={12}>
                <Divider
                  label={t('register.addressLabel')}
                  labelPosition="left"
                />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                <NationSelector required {...form.getInputProps('country')} />
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                {form.values.country === 'Italia' ? (
                  <ProvinceSelector
                    required
                    {...form.getInputProps('province')}
                  />
                ) : (
                  <TextInput
                    label={t('register.province')}
                    placeholder={t('register.provincePlaceholder')}
                    required
                    {...form.getInputProps('province')}
                  />
                )}
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                {form.values.country === 'Italia' ? (
                  <CitySelector
                    province={form.values.province}
                    disabled={form.values.province === ''}
                    required
                    {...form.getInputProps('city')}
                  />
                ) : (
                  <TextInput
                    label={t('register.city')}
                    placeholder={t('register.cityPlaceholder')}
                    required
                    {...form.getInputProps('city')}
                  />
                )}
              </Grid.Col>

              <Grid.Col span={{ md: 6 }}>
                {form.values.country === 'Italia' ? (
                  <ZipSelector
                    city={form.values.city}
                    disabled={form.values.city === ''}
                    required
                    {...form.getInputProps('postalCode')}
                  />
                ) : (
                  <TextInput
                    label={t('register.postalCode')}
                    placeholder={t('register.postalCodePlaceholder')}
                    required
                    {...form.getInputProps('postalCode')}
                  />
                )}
              </Grid.Col>
              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('register.address')}
                  placeholder={t('register.addressPlaceholder')}
                  required
                  {...form.getInputProps('address')}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <Grid>
              <Grid.Col span={12}>
                <Divider
                  label={t('register.additionalInfoLabel')}
                  labelPosition="left"
                />
              </Grid.Col>

              {form.values.allowedCategories[0] === 'Ricerca' && (
                <>
                  <Grid.Col span={{ md: 6 }}>
                    <TechniqueSelector
                      {...form.getInputProps('techniquesUsed')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ md: 6 }}>
                    <GoodfellowSelector {...form.getInputProps('goodfellow')} />
                  </Grid.Col>

                  <Grid.Col span={{ md: 6 }}>
                    <OrganizationTypeSelector
                      {...form.getInputProps('organizationType')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ md: 6 }}>
                    <DiseaseSelector {...form.getInputProps('disease')} />
                  </Grid.Col>
                </>
              )}

              {form.values.allowedCategories[0] !== 'Ricerca' && (
                <Grid.Col span={{ md: 6 }}>
                  <InterestSectorSelector
                    {...form.getInputProps('interestSector')}
                    marketCategoryName={form.values.allowedCategories[0]}
                  />
                </Grid.Col>
              )}

              <Grid.Col span={6}>
                <MultiSelect
                  label={t('register.otherMarketsInterest')}
                  data={marketCategories
                    .filter((c) => c.name !== form.values.allowedCategories[0])
                    .map((category) => ({
                      label: category.name,
                      value: category.name,
                    }))}
                  disabled={isMarketCategoriesLoading}
                  placeholder={
                    form.values.allowedCategories.length !== 1
                      ? undefined
                      : t('register.otherMarketsPlaceholder')
                  }
                  error={form.errors.allowedCategories}
                  value={form.values.allowedCategories.slice(1)}
                  onChange={(value) => {
                    form.setFieldValue('allowedCategories', [
                      form.values.allowedCategories[0],
                      ...value,
                    ]);
                  }}
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Textarea
                  label={t('register.additionalNotes')}
                  placeholder={t('register.additionalNotesPlaceholder')}
                  {...form.getInputProps('additionalNotes')}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>

        <Group justify="center" mt="md">
          <Checkbox
            mt="xl"
            label={
              <Text size="sm">
                {t('register.privacyPolicyText')}{' '}
                <Anchor
                  href="/privacy-policy"
                  target="_blank"
                  underline="not-hover"
                >
                  {t('register.privacyPolicy')}
                </Anchor>{' '}
                {t('register.andConsentText')}{' '}
                <Text span c="red">
                  {t('register.required')}
                </Text>
              </Text>
            }
            required
          />
        </Group>

        {form.errors.general && (
          <Text ta="center" c="red" size="sm" mt="xl">
            {form.errors.general}
          </Text>
        )}

        <Button
          type="submit"
          fullWidth
          maw="10rem"
          mx="auto"
          mt="xl"
          loading={isLoading}
        >
          {t('register.signUpButton')}
        </Button>
      </form>
    </Container>
  );
}
